import {
  applyAgenticChatTabTitle,
  archiveAgenticChatHistorySession,
  dismissAgenticChatHistorySession,
  renameAgenticChatHistorySession,
  selectAgenticChatHistorySession,
} from 'features/embeddedSidebar/chatHistory/agenticChatHistoryActions'
import type { RecordedAgenticChatSession } from 'features/embeddedSidebar/hooks/useAgenticChatSessionHistory'
import type { DocChatConversation } from 'features/knowledgeDocumentViewer/types'
import { useWorkspaceTabs } from 'features/workspaceTabs/contexts/WorkspaceTabsContext'
import { useCallback } from 'react'

export function useAgenticChatHistoryActions({
  hydrateSession,
  onArchiveSession,
  onDeleteSession,
  onRemoveLocalSession,
  onRenameSession,
  siteId,
}: {
  hydrateSession: (
    session: RecordedAgenticChatSession
  ) => Promise<DocChatConversation | null>
  onArchiveSession: (sessionKey: string) => Promise<boolean>
  onDeleteSession: (sessionKey: string) => Promise<boolean>
  onRemoveLocalSession: (sessionKey: string) => void
  onRenameSession: (sessionKey: string, title: string) => void
  siteId: string | undefined
}): {
  archiveSession: (sessionKey: string, openTabId?: string) => void
  deleteSession: (sessionKey: string, openTabId?: string) => void
  renameSession: (sessionKey: string, title: string, openTabId?: string) => void
  selectSession: (
    session: RecordedAgenticChatSession,
    openTabId?: string
  ) => void
} {
  const {
    activateWorkspaceTab,
    closeWorkspaceTab,
    openAgenticConversation,
    setOpenTabs,
  } = useWorkspaceTabs()

  const selectSession = useCallback(
    (session: RecordedAgenticChatSession, openTabId?: string) => {
      void selectAgenticChatHistorySession({
        activateWorkspaceTab,
        hydrateSession,
        openAgenticConversation,
        openTabId,
        session,
        siteId,
      })
    },
    [activateWorkspaceTab, hydrateSession, openAgenticConversation, siteId]
  )

  const archiveSession = useCallback(
    (sessionKey: string, openTabId?: string) => {
      void archiveAgenticChatHistorySession({
        closeWorkspaceTab,
        onArchiveSession,
        onRemoveLocalSession,
        openTabId,
        sessionKey,
      })
    },
    [closeWorkspaceTab, onArchiveSession, onRemoveLocalSession]
  )

  const deleteSession = useCallback(
    (sessionKey: string, openTabId?: string) => {
      void dismissAgenticChatHistorySession({
        closeWorkspaceTab,
        onPersist: onDeleteSession,
        onRemoveLocalSession,
        openTabId,
        sessionKey,
      })
    },
    [closeWorkspaceTab, onDeleteSession, onRemoveLocalSession]
  )

  const renameWorkspaceTab = useCallback(
    ({
      sessionKey,
      tabId,
      title,
    }: {
      sessionKey: string
      tabId?: string
      title: string
    }) => {
      setOpenTabs((currentTabs) =>
        applyAgenticChatTabTitle({
          openTabs: currentTabs,
          sessionKey,
          tabId,
          title,
        })
      )
    },
    [setOpenTabs]
  )

  const renameSession = useCallback(
    (sessionKey: string, title: string, openTabId?: string) => {
      renameAgenticChatHistorySession({
        onRenameSession,
        openTabId,
        renameWorkspaceTab,
        sessionKey,
        title,
      })
    },
    [onRenameSession, renameWorkspaceTab]
  )

  return { archiveSession, deleteSession, renameSession, selectSession }
}
